import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Activity,
  CheckCircle,
  Clock,
  AlertCircle,
  Wifi,
  WifiOff,
  ExternalLink,
  Database,
  Globe,
  Zap,
  FileText,
  Image,
  Server,
  TrendingUp
} from "lucide-react";

const API_BASE = "";

// Color palette matching Dashboard
const colors = {
  primary: '#6366f1',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6',
  fetch: '#3b82f6',
  parse: '#f59e0b',
  mcc: '#6366f1',
  clean: '#06b6d4',
};

function ProgressPage() {
  const [progressData, setProgressData] = useState(null);
  const [merchantProgress, setMerchantProgress] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [crawlID, setCrawlID] = useState(null);
  const wsRef = useRef(null);

  // WebSocket connection management
  const connectWebSocket = () => {
    if (wsRef.current) {
      wsRef.current.close();
    }

    const ws = new WebSocket('/api/fetchers/ws');

    ws.onopen = () => {
      console.log('WebSocket connected');
      setWsConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        handleWebSocketMessage(message);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setWsConnected(false);
      setCrawlID(null);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setWsConnected(false);
    };

    wsRef.current = ws;
  };

  const disconnectWebSocket = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setWsConnected(false);
    setCrawlID(null);
  };

  const handleWebSocketMessage = (message) => {
    console.log('WebSocket message:', message);

    switch (message.type) {
      case 'connected':
        setCrawlID(message.data.crawl_id);
        break;
      case 'progress':
        setProgressData(message.data);
        break;
      default:
        console.log('Unknown message type:', message.type);
    }
  };

  useEffect(() => {
    // Connect WebSocket on component mount
    connectWebSocket();

    return () => {
      disconnectWebSocket();
    };
  }, []);

  const fetchProgress = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/fetchers/progress`);
      if (response.ok) {
        const data = await response.json();
        setProgressData(data);
      }
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    }
  };

  const fetchMerchantProgress = async () => {
    try {
      // Fetch merchant progress data from parsers module
      const response = await fetch(`${API_BASE}/api/merchants/progress`);
      if (response.ok) {
        const data = await response.json();
        console.log('Merchant progress data:', data);
        
        // Map API response to UI format
        if (Array.isArray(data)) {
          const mappedData = data.map(item => ({
            merchant_name: item.merchant_name || 'Unknown',
            url: item.url || '',
            fetch_status: item.stages?.fetch?.status || 'pending',
            parse_status: item.stages?.parse?.status || 'pending',
            mcc_status: item.stages?.mcc?.status || 'pending',
            fetch_duration: item.stages?.fetch?.duration || 0,
            parse_duration: item.stages?.parse?.duration || 0,
            mcc_duration: item.stages?.mcc?.duration || 0,
            status: item.status || 'pending',
            progress_percentage: item.progress_percentage || 0,
            elapsed_time: item.elapsed_time || 0,
            started_at: item.started_at
          }));
          
          // Deduplicate by URL - keep only the latest entry for each unique URL
          const uniqueMerchants = {};
          mappedData.forEach(merchant => {
            const url = merchant.url;
            if (!uniqueMerchants[url] || new Date(merchant.started_at) > new Date(uniqueMerchants[url].started_at)) {
              uniqueMerchants[url] = merchant;
            }
          });
          
          // Convert to array and sort by most recent first
          const deduplicatedData = Object.values(uniqueMerchants).sort((a, b) => {
            return new Date(b.started_at) - new Date(a.started_at);
          });
          
          console.log('Deduplicated merchant data:', deduplicatedData.slice(0, 5)); // Log first 5 for debugging
          setMerchantProgress(deduplicatedData);
        }
      }
    } catch (error) {
      console.error('Failed to fetch merchant progress:', error);
    }
  };

  useEffect(() => {
    fetchProgress();
    fetchMerchantProgress();
    const interval = setInterval(() => {
      fetchProgress();
      fetchMerchantProgress();
    }, 5000); // Update every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'processing':
      case 'storing':
      case 'cleaning':
        return <Activity className="w-5 h-5 text-blue-500 animate-pulse" />;
      case 'fetching':
        return <Clock className="w-5 h-5 text-orange-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'completed':
        return 'default';
      case 'error':
        return 'destructive';
      case 'processing':
      case 'storing':
      case 'cleaning':
      case 'fetching':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds < 0) return '0.0s';
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds.toFixed(1)}s`;
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'fetch': return colors.fetch;
      case 'parse': return colors.parse;
      case 'mcc': return colors.mcc;
      case 'clean': return colors.clean;
      case 'completed': return colors.success;
      case 'error': return colors.error;
      default: return colors.info;
    }
  };

  const MerchantProgressRow = ({ merchant }) => {
    const getProgressPercentage = () => {
      const stages = ['fetch', 'parse', 'mcc'];
      const completed = stages.filter(stage => merchant[`${stage}_status`] === 'completed').length;
      return (completed / stages.length) * 100;
    };

    return (
      <div className="border rounded-lg p-4 mb-3 hover:shadow-md transition-shadow bg-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <Globe className="w-5 h-5 text-blue-600" />
            <div>
              <h4 className="font-semibold text-sm">{merchant.merchant_name}</h4>
              <p className="text-xs text-gray-500">{merchant.url}</p>
            </div>
          </div>
          <Badge variant={merchant.fetch_status === 'completed' ? 'default' : 'secondary'}>
            {Math.round(getProgressPercentage())}% Complete
          </Badge>
        </div>

        <Progress value={getProgressPercentage()} className="mb-3" />

        <div className="grid grid-cols-3 gap-2">
          {/* Fetch Stage */}
          <div className={`p-3 rounded-lg border-2 ${
            merchant.fetch_status === 'completed' 
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-200 bg-gray-50'
          }`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-gray-700">Fetch</span>
              {merchant.fetch_status === 'completed' ? (
                <CheckCircle className="w-4 h-4 text-blue-600" />
              ) : (
                <Clock className="w-4 h-4 text-gray-400" />
              )}
            </div>
            <div className="text-xs text-gray-600">
              {merchant.fetch_duration ? formatTime(merchant.fetch_duration) : '-'}
            </div>
          </div>

          {/* Parse Stage */}
          <div className={`p-3 rounded-lg border-2 ${
            merchant.parse_status === 'completed' 
              ? 'border-orange-500 bg-orange-50' 
              : 'border-gray-200 bg-gray-50'
          }`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-gray-700">Parse</span>
              {merchant.parse_status === 'completed' ? (
                <CheckCircle className="w-4 h-4 text-orange-600" />
              ) : (
                <Clock className="w-4 h-4 text-gray-400" />
              )}
            </div>
            <div className="text-xs text-gray-600">
              {merchant.parse_duration ? formatTime(merchant.parse_duration) : '-'}
            </div>
          </div>

          {/* MCC Stage */}
          <div className={`p-3 rounded-lg border-2 ${
            merchant.mcc_status === 'completed' 
              ? 'border-purple-500 bg-purple-50' 
              : 'border-gray-200 bg-gray-50'
          }`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-gray-700">MCC</span>
              {merchant.mcc_status === 'completed' ? (
                <CheckCircle className="w-4 h-4 text-purple-600" />
              ) : (
                <Clock className="w-4 h-4 text-gray-400" />
              )}
            </div>
            <div className="text-xs text-gray-600">
              {merchant.mcc_duration ? formatTime(merchant.mcc_duration) : '-'}
            </div>
          </div>
        </div>

        {/* Total Time */}
        <div className="mt-3 pt-3 border-t flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-600">Total Processing Time</span>
          <span className="text-sm font-bold text-purple-600">
            {formatTime((merchant.fetch_duration || 0) + (merchant.parse_duration || 0) + (merchant.mcc_duration || 0))}
          </span>
        </div>
      </div>
    );
  };

  const getStageInfo = (status) => {
    switch (status) {
      case 'initializing':
        return { name: 'Initializing', description: 'Setting up crawl environment', icon: Zap };
      case 'discovering':
        return { name: 'Discovery', description: 'Finding URLs on the website', icon: Globe };
      case 'processing':
        return { name: 'Processing', description: 'Fetching and analyzing pages', icon: Activity };
      case 'completed':
        return { name: 'Completed', description: 'Crawl finished successfully', icon: CheckCircle };
      case 'error':
        return { name: 'Error', description: 'Crawl encountered an error', icon: AlertCircle };
      default:
        return { name: 'Unknown', description: 'Unknown status', icon: Clock };
    }
  };

  const serviceHealth = [
    { name: 'PostgreSQL', status: 'running', port: 5432, icon: Database },
    { name: 'MinIO', status: 'running', port: 9000, icon: FileText },
    { name: 'Fetchers', status: 'running', port: 8081, icon: Server },
    { name: 'Parsers', status: 'running', port: 8082, icon: FileText },
    { name: 'MCC', status: 'running', port: 8083, icon: Database },
    { name: 'Playwright', status: 'running', port: 8084, icon: Image },
    { name: 'Frontend', status: 'running', port: 3000, icon: Globe }
  ];

  return (
    <div className="max-w-[1500px] mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Progress Tracking</h1>
          <p className="text-muted-foreground">
            Real-time monitoring of crawl operations and system health
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {wsConnected ? (
            <Badge variant="default" className="flex items-center space-x-1">
              <Wifi className="w-3 h-3" />
              <span>Connected</span>
            </Badge>
          ) : (
            <Badge variant="destructive" className="flex items-center space-x-1">
              <WifiOff className="w-3 h-3" />
              <span>Disconnected</span>
            </Badge>
          )}
          {crawlID && (
            <Badge variant="outline" className="text-xs">
              ID: {crawlID}
            </Badge>
          )}
        </div>
      </div>

      <Tabs defaultValue="progress" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="progress">Crawl Progress</TabsTrigger>
          <TabsTrigger value="stages">Pipeline Stages</TabsTrigger>
          <TabsTrigger value="health">System Health</TabsTrigger>
        </TabsList>

        <TabsContent value="progress" className="space-y-6">
          {/* Merchant Progress Summary Cards */}
          {merchantProgress.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-full bg-blue-100">
                      <Database className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{merchantProgress.length}</p>
                      <p className="text-xs text-muted-foreground">Total Merchants</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-full bg-green-100">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">
                        {merchantProgress.filter(m => m.fetch_status === 'completed' && m.parse_status === 'completed').length}
                      </p>
                      <p className="text-xs text-muted-foreground">Completed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-full bg-orange-100">
                      <Activity className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">
                        {merchantProgress.filter(m => m.fetch_status === 'pending' || m.parse_status === 'pending').length}
                      </p>
                      <p className="text-xs text-muted-foreground">In Progress</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-full bg-purple-100">
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">
                        {merchantProgress.length > 0 
                          ? `${Math.round((merchantProgress.filter(m => m.fetch_status === 'completed' && m.parse_status === 'completed').length / merchantProgress.length) * 100)}%`
                          : '0%'
                        }
                      </p>
                      <p className="text-xs text-muted-foreground">Success Rate</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Merchant Progress List */}
          {merchantProgress.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Merchant Processing Status</CardTitle>
                <CardDescription>
                  Showing latest {Math.min(50, merchantProgress.length)} of {merchantProgress.length} merchants
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {merchantProgress.slice(0, 50).map((merchant, index) => (
                    <MerchantProgressRow key={merchant.url || index} merchant={merchant} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="stages" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Pipeline Stages</CardTitle>
              <CardDescription>
                Current status of each processing stage in the ATS pipeline
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { stage: 'initializing', label: 'Initialize', description: 'Setup crawl environment' },
                  { stage: 'discovering', label: 'Discover', description: 'Find URLs on website' },
                  { stage: 'processing', label: 'Process', description: 'Fetch and analyze pages' },
                  { stage: 'completed', label: 'Complete', description: 'Crawl finished' }
                ].map((stageInfo, index) => {
                  const isActive = progressData?.status === stageInfo.stage;
                  const isCompleted = progressData && (
                    (stageInfo.stage === 'initializing' && ['discovering', 'processing', 'completed'].includes(progressData.status)) ||
                    (stageInfo.stage === 'discovering' && ['processing', 'completed'].includes(progressData.status)) ||
                    (stageInfo.stage === 'processing' && progressData.status === 'completed') ||
                    progressData.status === 'completed'
                  );

                  return (
                    <div
                      key={stageInfo.stage}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        isActive
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-950'
                          : isCompleted
                          ? 'border-green-500 bg-green-50 dark:bg-green-950'
                          : 'border-gray-200 bg-gray-50 dark:bg-gray-900'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-full ${
                          isActive
                            ? 'bg-blue-500 text-white'
                            : isCompleted
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-300 text-gray-600'
                        }`}>
                          {isActive ? (
                            <Activity className="w-4 h-4 animate-pulse" />
                          ) : isCompleted ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <Clock className="w-4 h-4" />
                          )}
                        </div>
                        <div>
                          <h4 className="font-semibold">{stageInfo.label}</h4>
                          <p className="text-sm text-muted-foreground">{stageInfo.description}</p>
                        </div>
                      </div>
                      {isActive && progressData && (
                        <div className="mt-3">
                          <Progress value={((progressData.processed_urls || 0) / (progressData.total_urls || 1)) * 100} />
                          <p className="text-xs text-muted-foreground mt-1">
                            {progressData.processed_urls || 0} / {progressData.total_urls || 0} URLs
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="health" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>System Health</CardTitle>
              <CardDescription>
                Status of all MCC system services and components
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {serviceHealth.map((service, index) => {
                  const IconComponent = service.icon;
                  return (
                    <Card key={index} className="p-4">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-full ${
                          service.status === 'running'
                            ? 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400'
                            : 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400'
                        }`}>
                          <IconComponent className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold">{service.name}</h4>
                          <p className="text-sm text-muted-foreground">Port: {service.port}</p>
                        </div>
                        <Badge variant={service.status === 'running' ? 'default' : 'destructive'}>
                          {service.status}
                        </Badge>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default ProgressPage;